import React, { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { styled, Tooltip } from "@mui/material";
import {
  KeyboardCapslock,
  VisibilityOutlined,
  VisibilityOffOutlined,
} from "@mui/icons-material";

import {
  validateEmailToken,
  resetPassword,
} from "../../services/PasswordReset/PasswordResetServices";

import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
} from "mpulse-design-system";
import BackToSignInButton from "../BackToSignInButton/BackToSignInButton";
import LoadingData from "../LoadingData/LoadingData";
import constants from "../../constants";

export const PasswordReset = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [password, setPassword] = React.useState("");
  const [passwordConfirm, setPasswordConfirm] = React.useState("");
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [passwordConfirmVisible, setPasswordConfirmVisible] =
    React.useState(false);
  const [passwordError, setPasswordError] = React.useState(null);
  const [passwordLength, setPasswordLength] = React.useState(false);
  const [passwordHasNumber, setPasswordHasNumber] = React.useState(false);
  const [passwordHasUpper, setPasswordHasUpper] = React.useState(false);
  const [passwordHasSpecial, setPasswordHasSpecial] = React.useState(false);
  const [passwordMatchError, setPasswordMatchError] = React.useState(false);
  const [tokenIsValid, setTokenIsValid] = React.useState(false);
  const [tokenInvalidOrExpired, setTokenInvalidOrExpired] =
    React.useState(false);
  const [resetSuccessful, setResetSuccessful] = React.useState(false);
  const [resetError, setResetError] = React.useState(false);
  const [capslockIcon, setCapslockIcon] = React.useState(false);
  const [searchTimer, setSearchTimer] = React.useState(0);

  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const specialChars = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;

  const navigate = useNavigate();

  useEffect(() => {
    validateEmailToken(token)
      .then((res) => {
        setTokenIsValid(true);
      })
      .catch((e) => {
        setTokenInvalidOrExpired(true);
      });
  }, []);

  const sendPasswordReset = () => {
    setResetError(false);

    if (password !== passwordConfirm) {
      setPasswordMatchError(true);
      return;
    }

    resetPassword(token, password)
      .then((res) => {
        setResetSuccessful(true);
      })
      .catch((e) => {
        setResetError(true);
      });
  };

  const validatePassword = (pass) => {
    const passLength = pass.length >= 8;
    const hasNum = /\d/.test(pass);
    const hasCapital = /[A-Z]/.test(pass);
    const hasSpecial = specialChars.test(pass);
    const errSum =
      Number(!passLength) +
      Number(!hasNum) +
      Number(!hasCapital) +
      Number(!hasSpecial);

    passLength ? setPasswordLength(true) : setPasswordLength(false);
    hasNum ? setPasswordHasNumber(true) : setPasswordHasNumber(false);
    hasCapital ? setPasswordHasUpper(true) : setPasswordHasUpper(false);
    hasSpecial ? setPasswordHasSpecial(true) : setPasswordHasSpecial(false);

    setPasswordError(
      errSum == 0
        ? null
        : errSum > 2
        ? constants.resetPassword.errors.multipleErrors
        : !hasSpecial
        ? constants.resetPassword.errors.specialCharacter
        : !passLength
        ? constants.resetPassword.errors.minimumCharacters
        : !hasNum
        ? constants.resetPassword.errors.containsNumber
        : !hasCapital
        ? constants.resetPassword.errors.containsUpper
        : null
    );
  };

  const handlePasswordChange = (e) => {
    setPasswordMatchError(false);
    setResetError(false);

    clearTimeout(searchTimer);
    setSearchTimer(
      setTimeout(() => {
        setPassword(e.target.value);
        validatePassword(e.target.value);
      }, 2000)
    );
  };

  const handleConfirmChange = (e) => {
    setPasswordConfirm(e.target.value);
    setResetError(false);
    setPasswordMatchError(false);
  };

  const handleShowPassword = (e) => {
    setPasswordVisible(!passwordVisible);
  };

  const handleShowPasswordConfirm = (e) => {
    setPasswordConfirmVisible(!passwordConfirmVisible);
  };

  const checkCapslock = (e) => {
    if (e.getModifierState("CapsLock")) {
      setCapslockIcon(true);
    } else {
      setCapslockIcon(false);
    }
  };

  const PasswordLabel = ({ label, showPassword, handleShowPassword }) => {
    const passwordLabelIconProps = {
      sx: {
        paddingLeft: (theme) => theme.spacing(1),
        cursor: "pointer",
      },
      fontSize: "small",
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <span>{label}</span>
        <Tooltip
          variant="dark"
          arrow
          placement="bottom"
          title={showPassword ? "Hide" : "Preview"}
        >
          {showPassword ? (
            <VisibilityOffOutlined
              onClick={handleShowPassword}
              {...passwordLabelIconProps}
            />
          ) : (
            <VisibilityOutlined
              onClick={handleShowPassword}
              {...passwordLabelIconProps}
            />
          )}
        </Tooltip>
      </Box>
    );
  };

  const ResetSuccessful = () => (
    <StyledBox>
      <Typography variant="h1" color="textPrimary">
        Password Reset!
      </Typography>
      <Typography
        data-testid="success-message"
        color="textPrimary"
        sx={{ marginBottom: "8px", marginTop: "8px" }}
      >
        Your password has been set. You may go ahead and log in from your
        service now.
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ alignSelf: "center" }}>
        <BackToSignInButton />
      </Box>
    </StyledBox>
  );

  const TokenExpired = () => {
    return (
      <StyledBox>
        <Typography variant="h1" color="textPrimary">
          Link Expired
        </Typography>
        <Typography
          data-testid="success-message"
          color="textPrimary"
          sx={{ marginBottom: "8px", marginTop: "8px" }}
        >
          Sorry, your link has expired. Please reset your password again.
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ alignSelf: "center" }}>
          <BackToSignInButton />
        </Box>
      </StyledBox>
    );
  };

  if (!tokenIsValid && !tokenInvalidOrExpired) return <LoadingData />;

  return (
    <StyledPaper>
      {tokenInvalidOrExpired ? (
        <TokenExpired />
      ) : !resetSuccessful ? (
        <StyledBox>
          <Box>
            <Typography variant="h1" color="textPrimary">
              Reset Password
            </Typography>
            <Typography sx={{ fontWeight: "bold" }} color="textPrimary">
              {email}
            </Typography>
            <Typography>
              {constants.resetPassword.errors.strongPassword}
            </Typography>
            {resetError ? (
              <Alert severity="error">
                {constants.resetPassword.errors.troubleSaving}
              </Alert>
            ) : null}
          </Box>
          <Box>
            <StyledTextField
              id="password"
              color="secondary"
              defaultValue={null}
              placeholder={"Enter a new password"}
              type={passwordVisible ? "text" : "password"}
              onChange={handlePasswordChange}
              onKeyUp={checkCapslock}
              error={Boolean(passwordError) || passwordMatchError}
              inputProps={{ "data-testid": "reset-password1-input" }}
              InputProps={{
                endAdornment: capslockIcon && (
                  <Tooltip
                    variant="dark"
                    arrow
                    placement="right"
                    title="Caps lock is on"
                  >
                    <KeyboardCapslock fontSize="small" />
                  </Tooltip>
                ),
              }}
              helperText={passwordError}
              label={
                <PasswordLabel
                  label={"Password"}
                  showPassword={passwordVisible}
                  handleShowPassword={handleShowPassword}
                />
              }
            />
          </Box>
          <Box>
            <StyledTextField
              id="password-verify"
              color="secondary"
              defaultValue={null}
              placeholder={"Enter a new password again"}
              type={passwordConfirmVisible ? "text" : "password"}
              onChange={handleConfirmChange}
              error={passwordMatchError}
              inputProps={{ "data-testid": "reset-password2-input" }}
              InputProps={{
                endAdornment: capslockIcon && (
                  <Tooltip
                    variant="dark"
                    arrow
                    placement="right"
                    title="Caps lock is on"
                  >
                    <KeyboardCapslock fontSize="small" />
                  </Tooltip>
                ),
              }}
              helperText={
                passwordMatchError
                  ? constants.resetPassword.errors.dontMatch
                  : null
              }
              label={
                <PasswordLabel
                  label={"Confirm Password"}
                  showPassword={passwordConfirmVisible}
                  handleShowPassword={handleShowPasswordConfirm}
                />
              }
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Button
              data-testid="cancel-button"
              sx={{
                height: "40px",

                width: "84px",
                alignSelf: "flex-start",
                marginBottom: "0 !important",
              }}
              type="submit"
              variant="outlined"
              color="primary"
              onClick={(e) => navigate("login")}
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-button"
              sx={{
                height: "40px",
                width: "84px",
                alignSelf: "flex-end",
                marginBottom: "0 !important",
              }}
              type="submit"
              variant="contained"
              color="primary"
              onClick={sendPasswordReset}
              disabled={
                !(
                  passwordLength &&
                  passwordHasNumber &&
                  passwordHasUpper &&
                  passwordHasSpecial
                )
              }
            >
              Confirm
            </Button>
          </Box>
        </StyledBox>
      ) : (
        <ResetSuccessful />
      )}
    </StyledPaper>
  );
};

// Styling
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  height: "525px",
  display: "flex",
  flexDirection: "column",
  width: "325px",
  position: "relative",
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: "1",
  "& > *": {
    marginBottom: `${theme.spacing(4)} !important`,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  overflow: "hidden",
  label: {
    color: `${theme.palette.text.disabled} !important`,
  },
  "input::-webkit-caps-lock-indicator": {
    position: "absolute",
    left: "-15px",
  },
}));
